import { useEffect, useState } from 'react'
import { Organization } from 'lib/types'
import { saveFloorsToApi, saveSpacesToApi, deleteOldVenueData, savePOIsToApi } from 'models/api/mappedin'

type Props = {
  data: {
    org: Organization | null
    matchedFloors: any
    matchedSpaces: any
    matchedPOIs: any
    spaceMap: any
  }
  goBack: () => void
  onClose: () => void
}

type Status = 'idle' | 'loading' | 'success' | 'error'

export default function Step5SaveData({ data, goBack, onClose }: Props) {
  const [deleteStatus, setDeleteStatus] = useState<Status>('idle')
  const [floorStatus, setFloorStatus] = useState<Status>('idle')
  const [spaceStatus, setSpaceStatus] = useState<Status>('idle')
  const [poiStatus, setPoiStatus] = useState<Status>('idle')
  const [logs, setLogs] = useState<string[]>([])
  const [hasStarted, setHasStarted] = useState(false)

  const appendLog = (msg: string) => setLogs(prev => [...prev, msg])

  const runSetup = async () => {
    const orgId = data.org?.id
    if (!orgId) {
      appendLog('❌ No organization ID provided.')
      return
    }

    setHasStarted(true)

    try {
      appendLog('🗑️ Deleting old venue data...')
      setDeleteStatus('loading')
      await deleteOldVenueData(orgId)
      setDeleteStatus('success')
      appendLog('✅ Old venue data deleted.')

      appendLog('💾 Saving floors...')
      setFloorStatus('loading')
      await saveFloorsToApi(orgId, data.matchedFloors)
      setFloorStatus('success')
      appendLog('✅ Floors saved.')

      appendLog('💾 Saving spaces...')
      setSpaceStatus('loading')
      await saveSpacesToApi(orgId, data.matchedFloors, data.spaceMap)
      setSpaceStatus('success')
      appendLog('✅ Spaces saved.')

      appendLog('💾 Saving POIs...')
      setPoiStatus('loading')
      await savePOIsToApi(orgId, data.matchedPOIs, data.matchedFloors, data.matchedSpaces)
      setPoiStatus('success')
      appendLog('✅ POIs saved.')
    } catch (err) {
      appendLog(`❌ Error: ${err instanceof Error ? err.message : String(err)}`)
      setDeleteStatus(s => (s === 'loading' ? 'error' : s))
      setFloorStatus(s => (s === 'loading' ? 'error' : s))
      setSpaceStatus(s => (s === 'loading' ? 'error' : s))
      setPoiStatus(s => (s === 'loading' ? 'error' : s))
    }
  }

  const renderStatus = (status: Status) => {
    if (status === 'loading') return <span style={{ color: '#555' }}>⏳ Working...</span>
    if (status === 'success') return <span style={{ color: 'green' }}>✅ Success</span>
    if (status === 'error') return <span style={{ color: 'red' }}>❌ Failed</span>
    return null
  }

  return (
    <div>
      <h2>Step 5: Save Matched Data</h2>
      <p>This will delete old data and save your new floors, spaces, and POIs in sequence.</p>

      <button
        onClick={runSetup}
        disabled={hasStarted}
        style={{
          marginBottom: '1rem',
          padding: '0.75rem 1.5rem',
          backgroundColor: '#29a096',
          color: 'white',
          fontWeight: 'bold',
          border: 'none',
          borderRadius: '4px',
          cursor: hasStarted ? 'not-allowed' : 'pointer',
        }}
      >
        🚀 Run Setup
      </button>

      <ul style={{ listStyle: 'none', paddingLeft: 0 }}>
        <li>Delete Old Venue: {renderStatus(deleteStatus)}</li>
        <li>Save Floors: {renderStatus(floorStatus)}</li>
        <li>Save Spaces: {renderStatus(spaceStatus)}</li>
        <li>Save POIs: {renderStatus(poiStatus)}</li>
      </ul>

      <div
        style={{
          margin: '1.5rem 0',
          background: '#f9f9f9',
          padding: '1rem',
          borderRadius: '6px',
          maxHeight: '200px',
          overflowY: 'auto',
        }}
      >
        <h4>Logs:</h4>
        <pre style={{ fontSize: '0.9rem' }}>{logs.join('\n')}</pre>
      </div>

      <div>
        <button onClick={goBack} style={{ marginRight: '1rem', padding: '0.5rem 1rem', background: '#ddd' }}>
          ← Back
        </button>
        <button
          onClick={onClose}
          disabled={poiStatus !== 'success'}
          style={{
            padding: '0.5rem 1rem',
            background: poiStatus === 'success' ? '#29a096' : '#ccc',
            color: 'white',
            fontWeight: 'bold',
            border: 'none',
            borderRadius: '4px',
            cursor: poiStatus === 'success' ? 'pointer' : 'not-allowed',
          }}
        >
          Finish & Close
        </button>
      </div>
    </div>
  )
}
